import { API_URL } from "@/lib/api-client";
import { formatTokenCount } from "@/lib/format-datetime";
import type { ToastType } from "@/stores/useToastStore";

export interface ChatMetadata {
	id: string;
	title: string | null;
	createdAt: string;
	updatedAt: string;
	createdById: string;
	archived: boolean;
	projectId: string;
	projectName: string;
	/** null = 環境が削除された孤児チャット (アーカイブ済みのまま閲覧専用)。 */
	environmentId: string | null;
	environmentName: string | null;
	/** 環境を動かすデバイスの名前。クラウドの環境では null (旧サーバーでは未指定)。 */
	environmentRunnerName?: string | null;
	/** メッセージ追記の楽観的ロックに使う会話のリビジョン。送信時に expectedRevision として渡す。 */
	revision: number;
	hasLiveActivity?: boolean | null;
	/** チャットごとのツール実行確認レベル。旧サーバーとの互換期間は未指定になりうる。 */
	permissionMode?: PermissionMode;
}

export type PermissionMode = "acceptEdits" | "manual";

export type ChatListEntry = {
	id: string;
	title: string | null;
	createdAt: string;
	updatedAt: string;
	createdById: string;
	archived: boolean;
	projectId: string;
	projectName: string;
	/** null = 環境が削除された孤児チャット。 */
	environmentId: string | null;
	environmentName: string | null;
};

/**
 * サーバが見たチャットの状態。running = エージェント実行中、waitingUser = ツール承認または
 * HITL ツールの質問への回答待ち、failed = 最後のターンがエラーで終了、
 * idle = 実行しておらず操作も待っていない。
 */
export type ChatActivityState = "running" | "waitingUser" | "failed" | "idle";

/** 可視チャット 1 件の状態。 */
export type ChatActivityEntry = {
	chatId: string;
	state: ChatActivityState;
	/** 最終メッセージの時刻 (ISO)。ブラウザが持つ既読時刻と比べて未読を判定する。 */
	lastActivityAt: string;
};

/**
 * 左ペインのチャット一覧で示す状態。running = 実行中 (アクセントの丸)、waitingUser = ユーザーの
 * 操作待ち (注意の丸)、failed = エラーで終了して未読 (危険の丸)、unread = 正常に終了して未読 (題名の太字)。
 * 既読で落ち着いたチャットはこの型を持たない (何も示さない)。
 */
export type ChatIndicator = "running" | "waitingUser" | "failed" | "unread";

/** 直近のモデル応答時点における、会話コンテキストの使用量。 */
export interface ContextUsage {
	modelId: string;
	inputTokens: number;
	effectiveInputLimitTokens: number;
	/** モデルが提供する総コンテキスト上限。旧メッセージでは未保存の場合がある。 */
	contextWindowTokens?: number;
}

/** UI 表示に使うモデル総上限。旧形式では従来の実効入力上限へフォールバックする。 */
export function contextUsageDisplayLimitTokens(usage: ContextUsage): number {
	return usage.contextWindowTokens !== undefined && usage.contextWindowTokens > 0
		? usage.contextWindowTokens
		: usage.effectiveInputLimitTokens;
}

/** コンテキスト使用率を、表示用の 0〜100% の整数へ正規化する。 */
export function contextUsagePercent(usage: ContextUsage): number {
	const displayLimitTokens = contextUsageDisplayLimitTokens(usage);
	if (displayLimitTokens <= 0) return 0;
	return Math.min(100, Math.round((usage.inputTokens / displayLimitTokens) * 100));
}

/** Composer のコンテキスト使用量 tooltip をモデル総上限基準で整形する。 */
export function formatContextUsageTooltip(usage: ContextUsage): string {
	return `${formatTokenCount(usage.inputTokens)} / ${formatTokenCount(contextUsageDisplayLimitTokens(usage))}トークン`;
}

export interface ChatMessageDto {
	id: string;
	chatId: string;
	order: number;
	role: ChatMessageRole;
	content: ContentBlock[];
	createdAt: string;
	createdById: string | null;
	stopReason: ChatStopReason | null;
	stopDetails: string | null;
	contextUsage?: ContextUsage | null;
	clientRequestId?: string | null;
	toolResultMetadata?: ToolResultMetadata | null;
}

/** 生成中の断片の種別。thinkingStarted は「考察中」への切り替え合図で、本文は持たない。 */
export type ChatDeltaKind = "text" | "thinkingStarted" | "advisorStarted" | "toolCallStarted";

/**
 * 生成中の assistant 応答の断片。確定メッセージが届くまでの暫定表示に使う。
 * seq は試行内で単調増加するため、重複配信と巻き戻りは (jobId, attempt, seq) で弾く。
 * 再試行では seq が 0 から振り直されるので、attempt が変われば積み上げ済みの内容ごと捨てる。
 */
export interface ChatDeltaDto {
	jobId: string;
	attempt: number;
	seq: number;
	kind: ChatDeltaKind;
	toolName?: string | null;
	text: string | null;
}

/**
 * tool_result に付随する、エージェントには見えずユーザー向け UI にのみ表示するメタデータ。
 * `kind` で種類を判別する (現状は "fileDiffs" のみ)。
 */
export type ToolResultMetadata = FileDiffsMetadata;

/** テキストエディタツールの編集結果として生成された差分の一覧。 */
export interface FileDiffsMetadata {
	kind: "fileDiffs";
	diffs: FileDiff[];
}

/** エージェントのファイル編集ツールが生成した差分の 1 hunk。 */
export interface FileDiffHunk {
	oldStart: number;
	oldLines: number;
	newStart: number;
	newLines: number;
	lines: string[];
}

/** エージェントのファイル編集ツールが生成した、1 ファイル分の差分。 */
export interface FileDiff {
	toolUseId: string;
	path: string;
	isNew: boolean;
	hunks: FileDiffHunk[];
	truncated?: boolean;
}

/** hunk 群から追加・削除された行数を数える。 */
export function countDiffLines(hunks: FileDiffHunk[]): { added: number; removed: number } {
	let added = 0;
	let removed = 0;
	for (const hunk of hunks) {
		for (const line of hunk.lines) {
			if (line.startsWith("+")) added++;
			else if (line.startsWith("-")) removed++;
		}
	}
	return { added, removed };
}

export type ChatStopReason =
	| "endTurn"
	| "maxTokens"
	| "modelContextWindowExceeded"
	| "stopSequence"
	| "toolUse"
	| "pauseTurn"
	| "refusal";

export type ContentBlock =
	| TextContentBlock
	| ToolUseContentBlock
	| ToolResultContentBlock
	| ImageContentBlock
	| DocumentContentBlock
	| UploadedFileContentBlock
	| ReasoningContentBlock;

export interface ReasoningContentBlock {
	type: "reasoning";
	provider: string;
	model: string;
	id: string;
	summary: string[];
	encrypted_content: string;
}

export interface TextContentBlock {
	type: "text";
	text: string;
}

/** `ContentBlock` のうち tool_use ブロックを表す絞り込み型。`isToolUseBlock` で判定する。 */
export interface ToolUseContentBlock {
	type: "tool_use";
	id: string;
	name: string;
	input: Record<string, unknown>;
	/** toolset (browser) のメンバー呼び出しにだけ付く。name は screenshot / key 等のメンバー名。 */
	toolset_name?: string;
}

/** browser toolset の tool_result に添えるブラウザ状態 (タブ一覧)。出力抽出では無視され、表示にはまだ使わない。 */
export interface BrowserStateContentBlock {
	type: "browser_state";
	tabs: { tab_id: string; title: string; url: string; active: boolean }[];
	state_changes?: { type: string; [key: string]: unknown }[];
}

/** `ContentBlock` のうち tool_result ブロックを表す絞り込み型。`isToolResultBlock` で判定する。 */
export interface ToolResultContentBlock {
	type: "tool_result";
	tool_use_id: string;
	content?: (
		| TextContentBlock
		| ImageContentBlock
		| DocumentContentBlock
		| BrowserStateContentBlock
	)[];
	is_error?: boolean;
}

/** 履歴 API が本体を外した画像・文書の source。url は画像だけに付き、API_URL からの相対パス。 */
export interface OmittedMediaSource {
	media_type: string;
	byte_size: number;
}

export interface ImageContentBlock {
	type: "image";
	source:
		| {
				type: "base64";
				media_type: string;
				data: string;
		  }
		| (OmittedMediaSource & {
				type: "url";
				url: string;
				/** ヘッダから寸法を読めた画像だけに付く。 */
				width?: number;
				height?: number;
		  });
}

/** 文書ブロック。ユーザーの添付 (トップレベル) と、tool_result にネストした web_fetch の PDF で使う。 */
export interface DocumentContentBlock {
	type: "document";
	source:
		| {
				type: "base64" | "text";
				media_type: string;
				data: string;
		  }
		| (OmittedMediaSource & { type: "omitted" });
	title?: string;
}

/**
 * 本文に入れずワークスペースへ置いたチャット添付。ユーザーの発言ではないため text にせず、
 * バックエンドがモデルへ送るときにパスを伝えるテキストへ変換する。
 */
export interface UploadedFileContentBlock {
	type: "uploaded_file";
	/** `~/` から始まるワークスペース上のパス。 */
	path: string;
	size: number;
}

/** "skipped" はハーネスが実行せずエージェントへ差し戻した呼び出し (HITL 混在拒否)。 */
export type ToolCallStatus =
	| "running"
	| "completed"
	| "waiting_user"
	| "error"
	| "cancelled"
	| "skipped";

/** バックグラウンド サブエージェントの実行状態。backend の SubagentRunStatus に対応。 */
export type SubagentRunStatus =
	| "queued"
	| "running"
	| "cancelRequested"
	| "completed"
	| "failed"
	| "cancelled"
	| "timedOut";

/** GET /api/chats/{chatId}/subagent-runs のレスポンス要素。 */
export interface ChatSubagentRun {
	id: string;
	toolUseId: string;
	profile: string;
	model: string;
	status: SubagentRunStatus;
	description: string;
	isBackground: boolean;
	launchedByUserId: string | null;
	turns: number;
	toolCallCount: number;
	/** 未キャッシュ入力のみ。総入力は subagentTotalInputTokens() を使う。 */
	inputTokens: number;
	cacheCreationInputTokens: number;
	cacheReadInputTokens: number;
	outputTokens: number;
	startedAt: string;
	endedAt: string | null;
	/** 完了時にサブエージェントが返した最終応答。未完了・応答なしなら null。 */
	summary: string | null;
	error: string | null;
	phase: string | null;
	createdAt: string;
	parentJobId: string;
	/** この run を所有する workflow の id。通常の run_subagent 起動では null。 */
	workflowRunId: string | null;
	/** true のとき summary は一覧向けに先頭で切られている (全文は実行履歴の取得で届く)。 */
	summaryTruncated: boolean;
	/** true のとき error も一覧向けに先頭で切られている。 */
	errorTruncated: boolean;
	/** 停止された理由。停止経路は error を立てずにこちらへ入れるため、表示ではこちらへ落ちる。 */
	cancellationReason: string | null;
	/** workflow が所有する run では、担うノードの id。宣言ノード (declaredNodes) と突き合わせるキー。 */
	label: string | null;
}

export interface SubagentTranscriptMessage {
	id: string;
	order: number;
	role: Extract<ChatMessageRole, "user" | "assistant">;
	content: ContentBlock[];
	createdAt: string;
	stopReason: ChatStopReason | null;
}
export interface SubagentRunDetail {
	run: ChatSubagentRun;
	messages: SubagentTranscriptMessage[];
}
const TERMINAL_SUBAGENT_STATUSES = new Set<SubagentRunStatus>([
	"completed",
	"failed",
	"cancelled",
	"timedOut",
]);
/** サブエージェント実行が終端しているかを返す。 */
export function isTerminalSubagentStatus(status: SubagentRunStatus): boolean {
	return TERMINAL_SUBAGENT_STATUSES.has(status);
}

/**
 * run の経過時間を測れるか。終端したのに endedAt が無い行 (終端処理の取りこぼし) は測れない。
 * 終了時刻が無いまま描くと、止まっている run の経過が伸び続けるか 0 秒に潰れるかのどちらかになる。
 */
export function hasMeasurableElapsed(run: ChatSubagentRun): boolean {
	return !isTerminalSubagentStatus(run.status) || run.endedAt !== null;
}

const SUBAGENT_STATUS_TEXT: Record<SubagentRunStatus, string> = {
	queued: "待機中",
	running: "実行中",
	cancelRequested: "停止中",
	completed: "完了",
	failed: "失敗",
	cancelled: "停止済み",
	timedOut: "タイムアウト",
};

/** サブエージェント実行状態の表示名。 */
export function subagentStatusText(status: SubagentRunStatus): string {
	return SUBAGENT_STATUS_TEXT[status];
}

/** 実行が異常終了したか (ユーザーによる停止は含まない)。 */
export function isFailedSubagentStatus(status: SubagentRunStatus): boolean {
	return status === "failed" || status === "timedOut";
}

/**
 * 停止要求の結果から表示するトーストを決める。停止要求と正常終了が競合すると API は現在の
 * 終端状態をそのまま返すため、停止できた場合だけ成功として伝える。
 */
export function stopResultToast(
	description: string,
	status: SubagentRunStatus,
): { type: ToastType; message: string } {
	if (status === "cancelRequested") {
		return { type: "success", message: `「${description}」を停止しています。` };
	}
	if (status === "cancelled") {
		return { type: "success", message: `「${description}」を停止しました。` };
	}
	return {
		type: "info",
		message: `「${description}」は停止する前に${subagentStatusText(status)}していました。`,
	};
}

/**
 * 終端した実行の理由文。foreground / background を問わず、失敗の原因として利用者に見せる文言を返す。
 * 理由が残っていない正常終了・単純な停止では null。
 */
export function subagentFailureText(run: ChatSubagentRun): string | null {
	// backend は最大ターン数超過をセンチネル値で記録する。そのままでは意味が伝わらないため言い換える。
	if (run.error === "max_turns") return "最大ターン数に達したため停止しました。";
	if (run.error) return run.error;
	// タイムアウトは監視側が停止させるため error が残らない。状態から文言を補う。
	return run.status === "timedOut" ? "実行時間の上限に達したため停止しました。" : null;
}

/**
 * 最終応答も失敗理由も残らなかった終端実行に添える補足。
 * completed はツール実行だけで終わった場合、cancelled は停止によって結果自体が無い場合。
 */
export function subagentResultNote(status: SubagentRunStatus): string | null {
	if (status === "completed") return "結果を親エージェントに渡しました。";
	if (status === "cancelled") return "停止したため、結果はありません。";
	return null;
}

/**
 * 停止された理由。停止経路は error を立てずに cancellationReason だけを残すため、error だけを
 * 見るとクレジット枯渇や所有 workflow の終了が「停止済み」としか読めない。
 * 理由は停止要求の時点で書かれ、その後に自力で完走しても消えないので、停止として終端したときだけ返す。
 */
export function subagentStopReason(run: ChatSubagentRun): string | null {
	return run.status === "cancelled" ? run.cancellationReason : null;
}

/** ワークフロー実行の状態。backend の WorkflowRunStatus に対応 (サブエージェントと違い timedOut は無い)。 */
export type WorkflowRunStatus =
	| "queued"
	| "running"
	| "cancelRequested"
	| "completed"
	| "failed"
	| "cancelled";

/** workflow が宣言したノード 1 件の輪郭。phase は run 行の phase と同じく見出し。 */
export interface WorkflowDeclaredNode {
	id: string;
	description: string;
	phase: string | null;
}

/** GET /api/chats/{chatId}/workflow-runs のレスポンス要素。 */
export interface ChatWorkflowRun {
	id: string;
	/** 起動した run_workflow の tool_use id。チャットのツールカードと結び付ける唯一のキー。 */
	toolUseId: string;
	status: WorkflowRunStatus;
	/** 定義の name。カードの見出しに使う短い識別子。列の追加より前の行では空 ({@link workflowTitle})。 */
	name: string;
	description: string;
	launchedByUserId: string;
	/** 宣言順のフェーズ名。run がまだ 1 件も無いフェーズを輪郭として描くために使う。 */
	declaredPhases: string[];
	/** 宣言順のノードの輪郭。run 行が無いノード (空き枠待ち・後段のフェーズ) はここからしか分からない。 */
	declaredNodes: WorkflowDeclaredNode[];
	error: string | null;
	/** 停止された理由。停止経路では error が立たないため、表示ではこちらへ落ちる。 */
	cancellationReason: string | null;
	startedAt: string;
	endedAt: string | null;
	createdAt: string;
}

/** カードの見出し。name は後から足した列なので、無い行は説明で代用する。 */
export function workflowTitle(run: Pick<ChatWorkflowRun, "name" | "description">): string {
	return run.name || run.description;
}

/** ワークフローの状態表示。状態の意味はサブエージェントと同じなので語彙も揃える。 */
export function workflowStatusText(status: WorkflowRunStatus): string {
	return subagentStatusText(status);
}

/** ワークフローが終端したか。 */
export function isTerminalWorkflowStatus(status: WorkflowRunStatus): boolean {
	return isTerminalSubagentStatus(status);
}

/** ワークフローの経過時間を測れるか。判断は {@link hasMeasurableElapsed} と同じ。 */
export function hasMeasurableWorkflowElapsed(run: ChatWorkflowRun): boolean {
	return !isTerminalWorkflowStatus(run.status) || run.endedAt !== null;
}

/**
 * ワークフローが終わった理由。停止経路は error を立てずに cancellationReason だけを残すため、
 * error だけを見るとクレジット枯渇や管理者による停止が「停止済み」としか読めない。
 * ただし cancellationReason は停止要求の時点で書かれ、その後に自力で完走しても消えない
 * (WorkflowRunStore が Cancelled へ補正するのは Failed のときだけ)。状態で絞らないと、
 * 成功して終わった実行や停止処理中の実行が失敗として赤く描かれる。
 */
export function workflowFailureText(run: ChatWorkflowRun): string | null {
	if (run.error !== null) return run.error;
	return run.status === "cancelled" ? run.cancellationReason : null;
}

export type ChatMessageRole =
	| "user"
	| "assistant"
	| "compaction"
	| "systemError"
	| "systemNotice"
	| "monitorEvent"
	| "subagentEvent";

/**
 * 停止処理が、実行中だったサーバツールの tool_result に書き込む本文の接頭辞。本文は停止理由 (ユーザー・クレジット
 * 枯渇・保守) で変わるため、接頭辞で「中断された」ものとして扱う。
 * backend の AgentStopNotice.InterruptedToolResultPrefix と必ず同期すること。
 */
export const STOPPED_TOOL_RESULT_PREFIX = "[stopped] ";

export function isTextContent(block: ContentBlock): block is TextContentBlock {
	return block.type === "text";
}

export function isToolUseContent(block: ContentBlock): block is ToolUseContentBlock {
	return block.type === "tool_use";
}

export function isToolResultContent(block: ContentBlock): block is ToolResultContentBlock {
	return block.type === "tool_result";
}

export function isImageContent(block: ContentBlock): block is ImageContentBlock {
	return block.type === "image";
}

export function isDocumentContent(block: ContentBlock): block is DocumentContentBlock {
	return block.type === "document";
}

export function isUploadedFileContent(block: ContentBlock): block is UploadedFileContentBlock {
	return block.type === "uploaded_file";
}

/** クライアント実行の HITL (human-in-the-loop) ツール名 */
export const HITL_TOOL_NAMES: ReadonlySet<string> = new Set([
	"request_segmentation",
	"request_user_choice",
]);

/**
 * 1 レスポンスに HITL tool_use が他の tool_use と混在しているか。バックエンドはこのターンの HITL を
 * 保留にせず全 tool_use を拒否する (AgentService.RequiresHitlOnlyRejection と同じ述語)。
 */
export function isHitlRejectionTurn(content: readonly ContentBlock[]): boolean {
	const toolUses = content.filter(isToolUseContent);
	return toolUses.length > 1 && toolUses.some((b) => HITL_TOOL_NAMES.has(b.name));
}

/** 画像ブロックを <img src> に渡せる URL (本体を持つなら data URL) にする。 */
export function imageBlockSrc(image: ImageContentBlock): string {
	return image.source.type === "url"
		? `${API_URL}${image.source.url}`
		: `data:${image.source.media_type};base64,${image.source.data}`;
}

/**
 * monitorEvent メッセージ本文 (JSON エンベロープ) から、説明・内容・monitor_id を取り出す。
 * monitorId はどのモニターの出力かでログをまとめるために使う。
 * 期待する形でなければ説明と monitorId を null とし、内容は元のテキストをそのまま返す。
 */
export function parseMonitorEventText(text: string): {
	description: string | null;
	content: string;
	monitorId: string | null;
} {
	const trimmed = text.trim();
	if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
		try {
			const parsed = JSON.parse(trimmed) as Record<string, unknown>;
			if (parsed?.type === "monitor_event") {
				return {
					description: typeof parsed.description === "string" ? parsed.description : null,
					content: typeof parsed.text === "string" ? parsed.text : "",
					monitorId: typeof parsed.monitor_id === "string" ? parsed.monitor_id : null,
				};
			}
		} catch {
			// monitor_event JSON でなければ素のテキストとして表示する
		}
	}
	return { description: null, content: text, monitorId: null };
}

/** モニターの実行状態。バックエンドの MonitorStatus (camelCase 文字列化) に対応。 */
export type MonitorStatus = "active" | "stopped" | "exited" | "failed";

/** モニターの停止理由。バックエンドの MonitorStopReason (camelCase 文字列化) に対応。 */
export type MonitorStopReason =
	| "agentStopped"
	| "chatArchived"
	| "chatDeleted"
	| "exited"
	| "failed"
	| "processGone"
	| "maxLifetime"
	| "maxEmissions"
	| "maxOutputBytes";

/** GET /api/chats/{chatId}/monitors のレスポンス要素。 */
export interface ChatMonitor {
	id: string;
	description: string;
	source: "sandboxScript" | "trainingJob";
	status: MonitorStatus;
	stopReason: MonitorStopReason | null;
	exitCode: number | null;
	createdAt: string;
	startedAt: string | null;
	terminalAt: string | null;
}

export type AgentJobStatus =
	| "pending"
	| "running"
	| "cancelRequested"
	| "cancelled"
	| "failed"
	| "succeeded";

export interface RetractedMessage {
	id: string;
	createdById: string;
	text?: string;
}

/** サーバー側の OrderIndex 採番と同じく、現在末尾の order の次を返す。 */
export function nextMessageOrder(messages: readonly Pick<ChatMessageDto, "order">[]): number {
	return messages.length === 0 ? 0 : messages.at(-1)!.order + 1;
}

/** SystemNotice の stopDetails から撤回されたトリガーメッセージ情報を取り出す。 */
export function parseRetractedMessage(
	stopDetails: string | null | undefined,
): RetractedMessage | null {
	if (!stopDetails) return null;
	try {
		const parsed = JSON.parse(stopDetails) as {
			retractedMessage?: {
				id?: unknown;
				createdById?: unknown;
				text?: unknown;
			};
		};
		const retracted = parsed.retractedMessage;
		if (
			typeof retracted?.id !== "string" ||
			typeof retracted.createdById !== "string" ||
			(retracted.text !== undefined && typeof retracted.text !== "string")
		) {
			return null;
		}
		return {
			id: retracted.id,
			createdById: retracted.createdById,
			...(retracted.text === undefined ? {} : { text: retracted.text }),
		};
	} catch {
		return null;
	}
}

/**
 * ユーザーに可視化する通知の種別。backend の CompactionNotice.Kind* / AgentStopNotice.Kind* /
 * AdvisorNotice.Kind* / ProviderAccessNotice.*Kind と同期する。
 */
export type NoticeKind =
	| "compactionFailed"
	| "contextExhausted"
	| "creditExhaustedQueueDiscarded"
	| "maintenanceQueueDiscarded"
	| "projectCreditLimitQueueDiscarded"
	| "advisorPauseExhausted"
	| "creditExhausted"
	| "deviceRevoked"
	| "deviceUnauthenticated"
	| "ipNotAllowed";

const NOTICE_KINDS: ReadonlySet<string> = new Set<NoticeKind>([
	"compactionFailed",
	"contextExhausted",
	"creditExhaustedQueueDiscarded",
	"maintenanceQueueDiscarded",
	"projectCreditLimitQueueDiscarded",
	"advisorPauseExhausted",
	"creditExhausted",
	"deviceRevoked",
	"deviceUnauthenticated",
	"ipNotAllowed",
]);

/** ローカル gateway のアクセス拒否 (systemError で届く) か。警告トーンで表示する。 */
export function isProviderAccessNoticeKind(kind: NoticeKind): boolean {
	return (
		kind === "creditExhausted" ||
		kind === "deviceRevoked" ||
		kind === "deviceUnauthenticated" ||
		kind === "ipNotAllowed"
	);
}

/** stopDetails から `{"notice":{"kind":...}}` マーカーの生値を取り出す (描画可否は問わない)。 */
function noticeKindRaw(stopDetails: string | null | undefined): string | null {
	if (!stopDetails) return null;
	try {
		const parsed = JSON.parse(stopDetails) as {
			notice?: { kind?: unknown };
		};
		const kind = parsed.notice?.kind;
		return typeof kind === "string" ? kind : null;
	} catch {
		return null;
	}
}

/**
 * SystemNotice の stopDetails からユーザー可視通知の種別を取り出す。
 * マーカーを持つ行だけが UI に描画される (文言一致でない明示 opt-in)。
 * マーカーなし・不正な JSON・未知の kind は null を返し、従来どおり非表示にする。
 */
export function parseNoticeKind(stopDetails: string | null | undefined): NoticeKind | null {
	const kind = noticeKindRaw(stopDetails);
	return kind !== null && NOTICE_KINDS.has(kind) ? (kind as NoticeKind) : null;
}

/** SystemError の stopDetails がモデルプロバイダー一時障害のマーカーか。 */
export function isProviderOutageNotice(stopDetails: string | null | undefined): boolean {
	return noticeKindRaw(stopDetails) === "providerOverloaded";
}

/**
 * compaction の要約生成中を表す進行中マーカーか。
 * カードは描画せず (parseNoticeKind の対象外)、考察中インジケータの文言切り替えにだけ使う。
 */
export function isCompactingNotice(stopDetails: string | null | undefined): boolean {
	return noticeKindRaw(stopDetails) === "compacting";
}

/** モデルの推論量。値が多い順に強い推論を行う。 */
export const CHAT_EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const;
export type ChatEffort = (typeof CHAT_EFFORTS)[number];

export const SUBAGENT_PROFILES = ["explore", "plan", "coder"] as const;
export type SubagentProfile = (typeof SUBAGENT_PROFILES)[number];
export type SubagentModelSelection = Partial<Record<SubagentProfile, string>>;

const SUBAGENT_PROFILE_LABELS: Record<SubagentProfile, string> = {
	explore: "調査",
	plan: "計画",
	coder: "コーディング",
};

/**
 * run が読み込んだ入力トークンの総量。API の inputTokens はキャッシュ分を含まないため、
 * これだけを出すとキャッシュヒットの多い run で桁違いに小さく見える。
 */
export function subagentTotalInputTokens(run: ChatSubagentRun): number {
	return run.inputTokens + run.cacheCreationInputTokens + run.cacheReadInputTokens;
}

/** プロファイルの表示名。API は文字列で返すため、未知の値はそのまま出す。 */
export function subagentProfileLabel(profile: string): string {
	return SUBAGENT_PROFILE_LABELS[profile as SubagentProfile] ?? profile;
}

/** POST /api/chats/{chatId}/messages のリクエスト本体。単一のユーザー由来メッセージを追記する。 */
export interface ChatMessageAppendDto {
	clientRequestId: string;
	expectedRevision: number;
	content: ContentBlock[];
	/** モデル alias。未指定時はサーバーの既定モデルを使う。 */
	model?: string;
	/** モデルの推論量。モデルが非対応の場合は省略する。 */
	effort?: ChatEffort;
	/** profile ごとのサブエージェント用モデル alias。キー欠落は親モデルを継承する。 */
	subagentModels?: SubagentModelSelection;
	/** ブラウザのタイムゾーン (IANA 名)。エージェントが日時を伝えるときに使う。 */
	timeZone?: string;
}

/**
 * 送信がアクティブジョブ中でキュー化されたときに返る、送信待ちメッセージ 1 件の DTO。
 * `seq` はチャット内 FIFO の唯一のソートキー。並行 POST の応答順で UI が逆転しないよう、
 * フロントはこの値でキューを昇順ソートする。
 */
export interface QueuedChatMessageDto {
	id: string;
	content: ContentBlock[];
	createdAt: string;
	createdById: string | null;
	clientRequestId: string;
	seq: number;
}

/**
 * POST /api/chats/{chatId}/messages のレスポンス本体。追記後のメッセージと最新リビジョンを返す。
 * アクティブジョブ中の送信がキュー化された場合は `queued: true` と `queuedMessage` が入り、
 * `agentJobId` は積んだ先のジョブ id を指す。ジョブが無い冪等再応答では `agentJobId` は null。
 */
export interface ChatSendResultDto {
	messages: ChatMessageDto[];
	revision: number;
	agentJobId: string | null;
	agentJobStatus: AgentJobStatus | null;
	queued?: boolean;
	queuedMessage?: QueuedChatMessageDto | null;
}
